import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  ExternalLink,
  Headphones,
  Infinity as InfinityIcon,
  MapPin,
  Quote,
  ShieldCheck,
  Sparkles,
  Tv,
  Users,
  Wallet,
  Wifi,
  Zap,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/lib/hooks";
import PackageCard from "@/components/PackageCard";
import SpeedTest from "@/components/SpeedTest";
import { ServiceIcon } from "@/components/ServiceIcon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1744868562210-fffb7fa882d9?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85";

export default function Home() {
  const { t, ml, lang } = useLang();
  const [slide, setSlide] = useState(0);
  const { data: packages = [] } = useContent("/content/packages");
  const { data: services = [] } = useContent("/content/services");
  const { data: campaigns = [] } = useContent("/content/campaigns");
  const { data: faqs = [] } = useContent("/content/faqs");

  const slides = [
    { badge: t("hero.slide1Badge"), title: t("hero.slide1Title"), title2: t("hero.slide1Title2"), desc: t("hero.slide1Desc") },
    { badge: t("hero.slide2Badge"), title: t("hero.slide2Title"), title2: t("hero.slide2Title2"), desc: t("hero.slide2Desc") },
    { badge: t("hero.slide3Badge"), title: t("hero.slide3Title"), title2: t("hero.slide3Title2"), desc: t("hero.slide3Desc") },
  ];

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % slides.length), 5500);
    return () => clearInterval(id);
  }, [slides.length]);

  const featureItems = [
    { icon: Wallet, title: t("home.why1Title"), desc: t("home.why1Desc") },
    { icon: Headphones, title: t("home.why3Title"), desc: t("home.why3Desc") },
    { icon: InfinityIcon, title: t("home.why2Title"), desc: t("home.why2Desc") },
    { icon: Tv, title: t("home.why4Title"), desc: t("home.why4Desc") },
  ];

  const statsItems = [
    { icon: Users, value: "12.000+", label: t("home.statsSubscribers") },
    { icon: MapPin, value: "5+", label: t("home.statsCoverage") },
    { icon: Clock, value: "%99,9", label: t("home.statsUptime") },
    { icon: Headphones, value: "08:00-20:00", label: t("home.statsSupport") },
  ];

  const offerFeatures = [
    t("home.offerFeat1") || t("home.why1Title"),
    t("home.offerFeat2") || t("home.why1Desc"),
    t("home.offerFeat3") || t("home.why2Title"),
    t("home.offerFeat4") || t("home.why3Title"),
  ];

  const testimonials = [
    {
      name: "Mehmet Y.",
      text:
        lang === "tr"
          ? "Ortaca'da bu kadar hızlı internet beklemiyordum. Kurulum çok hızlı oldu, fiyat da çok uygun."
          : "I didn't expect such fast internet in Ortaca. Installation was quick and the price is great.",
    },
    {
      name: "Ayşe K.",
      text:
        lang === "tr"
          ? "Yerel destek gerçekten var. Sorun yaşadığımda aynı gün müdahale ettiler. Teşekkürler ÇağrıNET!"
          : "Local support is real. They responded the same day I had an issue. Thanks ÇağrıNET!",
    },
    {
      name: "Hüseyin D.",
      text:
        lang === "tr"
          ? "Taahhütsüz olması çok büyük avantaj. Gizli ücret yok, ne ödediğimi biliyorum. Tavsiye ederim."
          : "Being commitment-free is a huge advantage. No hidden fees, I know what I pay. Highly recommend.",
    },
  ];

  return (
    <div data-testid="home-page">
      {/* ===== HERO ===== */}
      <section className="relative min-h-[640px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#021616] via-[#062B2B]/90 to-[#062B2B]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#062B2B] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto flex min-h-[640px] max-w-7xl items-center px-4 py-32 sm:px-6 lg:px-8">
          <div className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-12">
            {/* LEFT — slide text + CTAs */}
            <div className="lg:col-span-7">
              {slides.map((s, i) => (
                <div key={i} className={i === slide ? "block animate-slide-fade" : "hidden"}>
                  <span className="inline-flex items-center gap-2 rounded-md border border-[#2DD4BF]/30 bg-[#2DD4BF]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#2DD4BF]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#2DD4BF]" />
                    {s.badge}
                  </span>
                  <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-5xl lg:text-[3.5rem]">
                    {s.title}{" "}
                    <span className="text-[#2DD4BF]">{s.title2}</span>
                  </h1>
                  <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-300 md:text-lg">{s.desc}</p>
                </div>
              ))}

              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/tarifeler"
                  data-testid="hero-cta-packages"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#2DD4BF] px-7 py-3.5 text-sm font-bold text-[#042E2E] transition-all hover:bg-[#14B8A6] hover:shadow-lg hover:shadow-[#2DD4BF]/20"
                >
                  {t("hero.ctaPackages")}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </Link>
                <a
                  href="https://cagrinetwork.com.tr/online_basvuru/#/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-basvuru"
                  className="inline-flex items-center gap-2 rounded-xl border border-[#2DD4BF] px-7 py-3.5 text-sm font-bold text-[#2DD4BF] transition-all hover:bg-[#2DD4BF] hover:text-[#042E2E]"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t("nav.applyOnline")}
                </a>
              </div>

              {/* Trust badges */}
              <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 border-t border-white/10 pt-7">
                <span className="flex items-center gap-2 text-sm font-medium text-zinc-300">
                  <Zap className="h-4 w-4 text-[#2DD4BF]" /> 100 Mbps
                </span>
                <span className="flex items-center gap-2 text-sm font-medium text-zinc-300">
                  <ShieldCheck className="h-4 w-4 text-[#2DD4BF]" /> %99,9
                </span>
                <span className="flex items-center gap-2 text-sm font-medium text-zinc-300">
                  <Headphones className="h-4 w-4 text-[#2DD4BF]" /> {t("hero.statSupport")}
                </span>
              </div>
            </div>

            {/* RIGHT — Speed Test widget */}
            <div className="lg:col-span-5">
              <SpeedTest />
            </div>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === slide ? "w-8 bg-[#2DD4BF]" : "w-1.5 bg-white/30 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ===== STATS BAND ===== */}
      <section className="border-b border-zinc-100 bg-white" data-testid="home-stats">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 divide-x divide-zinc-100 lg:grid-cols-4">
            {statsItems.map((stat, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-8 lg:px-8">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#F0FDFA] text-[#0D9488]">
                  <stat.icon className="h-6 w-6" />
                </span>
                <div>
                  <p className="font-display text-2xl font-extrabold text-[#062B2B]">{stat.value}</p>
                  <p className="text-xs font-medium text-zinc-500">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY US — split feature cards ===== */}
      <section className="bg-[#F8FAFB] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0D9488]">{t("home.whyTitle")}</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#062B2B] md:text-4xl">{t("home.whySubtitle")}</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-features">
            {featureItems.map((item) => (
              <div
                key={item.title}
                className="group rounded-2xl border border-zinc-100 bg-white p-7 transition-all duration-300 hover:border-[#2DD4BF]/50 hover:shadow-xl hover:shadow-[#0D9488]/5"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#0D9488] to-[#0891B2] text-white transition-transform duration-300 group-hover:scale-110">
                  <item.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-base font-bold text-[#062B2B]">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== OFFER BANNER ===== */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-2xl bg-[#062B2B]" data-testid="home-offer">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-10 lg:p-14">
              <span className="inline-flex items-center gap-2 rounded-md bg-[#2DD4BF]/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#2DD4BF]">
                <Sparkles className="h-3.5 w-3.5" />
                {t("hero.badge")}
              </span>
              <h2 className="mt-5 font-display text-3xl font-bold leading-tight text-white md:text-4xl">
                {t("home.packagesTitle")}
              </h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-zinc-400">{t("home.packagesSubtitle")}</p>
              <ul className="mt-8 space-y-3">
                {offerFeatures.map((feat, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2DD4BF] text-[#042E2E]">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    {feat}
                  </li>
                ))}
              </ul>
              <Link
                to="/tarifeler"
                data-testid="home-offer-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#2DD4BF] px-7 py-3.5 text-sm font-bold text-[#042E2E] transition-all hover:bg-[#14B8A6] hover:shadow-lg hover:shadow-[#2DD4BF]/20"
              >
                {t("home.allPackages")}
                <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            </div>
            <div className="relative hidden items-center justify-center border-s border-white/5 p-14 lg:flex">
              <div className="absolute -bottom-12 -end-12 h-48 w-48 rounded-full bg-[#2DD4BF]/10 blur-3xl" />
              <div className="absolute -top-12 -start-12 h-48 w-48 rounded-full bg-[#0891B2]/15 blur-3xl" />
              <div className="relative text-center">
                <Wifi className="mx-auto h-12 w-12 text-[#2DD4BF]" />
                <p className="mt-4 font-display text-7xl font-extrabold text-white">100</p>
                <p className="text-xl font-bold text-[#2DD4BF]">{t("packages.mbps")}</p>
                <div className="mx-auto mt-4 h-px w-16 bg-white/20" />
                <p className="mt-4 text-sm font-semibold uppercase tracking-[0.2em] text-zinc-400">{t("hero.statSpeed")}</p>
                <p className="mt-2 text-xs font-medium text-zinc-400">Daha Yüksek Hız İçin Firmamızla İletişime Geçiniz</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PACKAGES ===== */}
      {packages.length > 0 && (
        <section className="bg-[#F8FAFB] py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0D9488]">{t("nav.packages")}</span>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#062B2B] md:text-4xl">{t("home.packagesTitle")}</h2>
              <p className="mt-3 text-base text-gray-500 md:text-lg">{t("home.packagesSubtitle")}</p>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="home-packages-grid">
              {packages.slice(0, 3).map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== SERVICES — dark section ===== */}
      {services.length > 0 && (
        <section className="bg-[#062B2B] py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#2DD4BF]">{t("nav.services")}</span>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-white md:text-4xl">{t("home.servicesTitle")}</h2>
              <p className="mt-3 text-base text-zinc-400 md:text-lg">{t("home.servicesSubtitle")}</p>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-services-grid">
              {services.map((service) => (
                <Link
                  key={service.id}
                  to="/hizmetler"
                  className="group rounded-xl border border-white/8 bg-white/[0.03] p-7 transition-all duration-300 hover:border-[#2DD4BF]/40 hover:bg-white/[0.06]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2DD4BF]/10 text-[#2DD4BF] transition-colors group-hover:bg-[#2DD4BF] group-hover:text-[#042E2E]">
                    <ServiceIcon name={service.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-white">{ml(service.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400 line-clamp-2">{ml(service.description)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== CAMPAIGNS ===== */}
      {campaigns.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0D9488]">{t("nav.campaigns")}</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#062B2B] md:text-4xl">{t("home.campaignsTitle")}</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="home-campaigns-grid">
            {campaigns.slice(0, 3).map((campaign) => (
              <Link
                key={campaign.id}
                to="/kampanyalar"
                className="group overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm transition-all hover:shadow-xl"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={campaign.image}
                    alt=""
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block rounded-md bg-[#CCFBF1] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#0D9488]">
                    {ml(campaign.badge)}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-bold text-[#062B2B]">{ml(campaign.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-500 line-clamp-2">{ml(campaign.description)}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#0D9488]">
                    {t("campaigns.apply")}
                    <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ===== TESTIMONIALS ===== */}
      <section className="bg-[#F8FAFB] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0D9488]">
              {lang === "tr" ? "Görüşler" : lang === "en" ? "Reviews" : lang === "ru" ? "Отзывы" : "آراء"}
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#062B2B] md:text-4xl">
              {lang === "tr" ? "Memnun Müşterilerimiz" : lang === "en" ? "Happy Customers" : lang === "ru" ? "Довольные клиенты" : "عملاؤنا السعداء"}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {testimonials.map((item, i) => (
              <div key={i} className="rounded-2xl border border-zinc-100 bg-white p-7 shadow-sm">
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <svg key={s} className="h-4 w-4 text-[#2DD4BF]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <Quote className="mt-4 h-7 w-7 text-[#2DD4BF]/30" />
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{item.text}</p>
                <div className="mt-6 flex items-center gap-3 border-t border-zinc-100 pt-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#0D9488] to-[#0891B2] font-display font-bold text-white">
                    {item.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-[#062B2B]">{item.name}</p>
                    <p className="text-xs text-gray-400">ÇağrıNET {lang === "tr" ? "Müşterisi" : "Customer"}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ PREVIEW ===== */}
      {faqs.length > 0 && (
        <section className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0D9488]">{t("nav.faq")}</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#062B2B] md:text-4xl">{t("faq.title")}</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="home-faq-accordion">
            {faqs.slice(0, 5).map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="rounded-xl border border-zinc-200 bg-white px-6 data-[state=open]:border-[#2DD4BF] data-[state=open]:shadow-sm"
              >
                <AccordionTrigger className="text-start font-display text-base font-bold text-[#062B2B] hover:no-underline">
                  {ml(faq.question)}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-gray-500">{ml(faq.answer)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <Link to="/sss" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0D9488] hover:underline">
              {lang === "tr" ? "Tüm Soruları Gör" : "See All Questions"}
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </div>
        </section>
      )}

      {/* ===== FINAL CTA ===== */}
      <section className="relative overflow-hidden bg-[#062B2B] py-20">
        <div className="absolute inset-0 bg-radial-teal" />
        <div className="relative mx-auto max-w-2xl px-4 text-center sm:px-6 lg:px-8">
          <ShieldCheck className="mx-auto h-12 w-12 text-[#2DD4BF]" />
          <h2 className="mt-5 font-display text-3xl font-bold text-white md:text-4xl">{t("home.ctaTitle")}</h2>
          {/* <p className="mt-4 text-base text-zinc-400 md:text-lg">{t("home.ctaSubtitle")}</p> */}
          {/* <Link
            to="/iletisim"
            data-testid="home-final-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#2DD4BF] px-8 py-4 text-sm font-bold text-[#042E2E] transition-all hover:bg-[#14B8A6] hover:shadow-xl hover:shadow-[#2DD4BF]/20"
          >
            {t("home.ctaButton")}
            <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link> */}
        </div>
      </section>
    </div>
  );
}
